import { Box, Stack, Typography } from '@mui/material';
import { type ExistingStockItem } from '../entity';
import { StockItemActions } from './stock-item-actions';

type StockItemRowProps = {
  item: ExistingStockItem;
  formatAmount: (amount: number | string | undefined, fractionDigits?: number) => string;
};

export const StockItemRow = ({ item, formatAmount }: StockItemRowProps) => {
  const isMaterial = item.itemType === 'MATERIAL';
  const price = isMaterial ? item.totalPrice : item.retailPrice || 0;

  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: 'common.white',
        borderBottom: '1px solid',
        borderColor: 'grey.200',
        display: 'grid',
        gap: 1,
        gridTemplateAreas: '"name quantity" "details actions"',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        minHeight: 50,
        px: { xs: 2, sm: 3 },
        py: 1.75,
      }}
    >
      <Typography
        sx={{
          color: 'text.primary',
          fontSize: { xs: '1rem', sm: '1.1rem' },
          fontWeight: 700,
          gridArea: 'name',
          lineHeight: 1.2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {item.itemName}
      </Typography>

      <Typography
        sx={{
          color: 'text.primary',
          fontSize: { xs: '1rem', sm: '1.1rem' },
          fontWeight: 700,
          gridArea: 'quantity',
          lineHeight: 1.2,
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}
      >
        {isMaterial ? `${item.quantity} ${item.unit}` : `${item.packageCount} ks`}
      </Typography>

      <Stack
        alignItems="center"
        direction="row"
        flexWrap="wrap"
        sx={{ color: 'text.secondary', gridArea: 'details', minWidth: 0 }}
      >
        <Typography component="span" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          {formatAmount(price, 0)}
        </Typography>
        <Box component="span" sx={{ color: 'primary.main', fontSize: '1.15rem', mx: 1.5 }}>
          •
        </Box>
        <Typography component="span" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          {item.packageCount} bal.
        </Typography>
        <Box component="span" sx={{ color: 'primary.main', fontSize: '1.15rem', mx: 1.5 }}>
          •
        </Box>
        <Typography component="span" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          min. {item.threshold} ks
        </Typography>
      </Stack>

      <Box sx={{ gridArea: 'actions', justifySelf: 'end' }}>
        <StockItemActions {...item} />
      </Box>
    </Box>
  );
};
